import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Leaf, Home, Heart } from 'lucide-react';
import deskCleaningImage from '../assets/axis-desk-cleaning.jpg';
import moppingImage from '../assets/axis-mopping-cleaning.jpeg';

function AnimatedSection({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export default function About() {
  return (
    <section id="about" className="relative py-24 sm:py-32 bg-sand-50 overflow-hidden">
      {/* Decorative */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-axis-100/30 rounded-full -translate-y-1/2 translate-x-1/3 blur-3xl" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-axis-50/50 rounded-full translate-y-1/2 -translate-x-1/3 blur-3xl" />

      <div className="max-w-7xl mx-auto section-padding relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-20 items-center">
          {/* Left - Image Collage */}
          <AnimatedSection>
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {/* Main Image */}
                <div className="col-span-2 relative rounded-3xl overflow-hidden aspect-[16/10] shadow-2xl shadow-axis-900/10">
                  <img
                    src={deskCleaningImage}
                    alt="Clean workspace"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-axis-900/30 to-transparent" />
                </div>
                {/* Second Image */}
                <div className="relative rounded-2xl overflow-hidden aspect-square shadow-xl shadow-axis-900/10">
                  <img
                    src={moppingImage}
                    alt="Clean office"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Stats Card */}
                <div id="certificate" className="relative rounded-2xl overflow-hidden aspect-square clean-gradient flex items-center justify-center p-6 shadow-xl shadow-axis-500/20 scroll-mt-28">
                  <div className="text-center text-white">
                    <div className="font-display text-5xl font-bold mb-1">100%</div>
                    <div className="text-sm font-medium text-white/80 uppercase tracking-wider">Satisfaction</div>
                    <div className="w-10 h-0.5 bg-white/30 mx-auto my-3" />
                    <div className="text-xs text-white/60">Bonded & Insured</div>
                  </div>
                </div>
              </div>
              {/* Floating Badge */}
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                className="absolute -bottom-6 -right-4 bg-white rounded-2xl shadow-xl shadow-axis-900/10 p-4 border border-axis-50"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-axis-50 flex items-center justify-center">
                    <Home className="w-6 h-6 text-axis-600" />
                  </div>
                  <div>
                    <div className="font-display text-lg font-semibold text-axis-900">Est. Local</div>
                    <div className="text-xs text-axis-600">Gaithersburg, MD</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </AnimatedSection>

          {/* Right - Content */}
          <AnimatedSection>
            <div className="lg:pl-4">
              <div className="inline-flex items-center gap-2 bg-axis-50 rounded-full px-4 py-1.5 mb-6">
                <Leaf className="w-4 h-4 text-axis-600" />
                <span className="text-sm font-medium text-axis-700">About Us</span>
              </div>

              <h2 className="font-display text-4xl sm:text-5xl font-bold text-axis-950 leading-tight mb-6">
                Making Your House
                <br />
                <span className="gradient-text italic">A True Home</span>
              </h2>

              <div className="space-y-5 mb-8">
                <p className="text-gray-600 text-lg leading-relaxed">
                  <strong className="text-axis-800">AXIS Cleaning Services, LLC</strong> is a residential and office cleaning company dedicated to delivering reliable, professional cleaning for your home and office. We offer comprehensive cleaning solutions to enhance the value of your property.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  We understand the multi-faceted challenges you face during the day. That's why we provide real-world solutions tailored to solve your cleaning problems. We believe you deserve to come home to a spotless, welcoming space every time.
                </p>
              </div>

              {/* Values */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: Heart, title: 'Care', desc: 'Every space treated with respect' },
                  { icon: Leaf, title: 'Freshness', desc: 'Thoroughly cleaned & sanitized' },
                  { icon: Home, title: 'Detail', desc: 'No corner left untouched' },
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-xl p-4 border border-axis-50 hover:shadow-lg hover:shadow-axis-500/5 transition-all duration-300 group">
                    <div className="w-10 h-10 rounded-lg bg-axis-50 flex items-center justify-center mb-3 group-hover:bg-axis-100 transition-colors">
                      <item.icon className="w-5 h-5 text-axis-600" />
                    </div>
                    <h4 className="font-display text-sm font-semibold text-axis-900 mb-1">{item.title}</h4>
                    <p className="text-xs text-gray-500">{item.desc}</p>
                  </div>
                ))}
              </div>

            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
}
